import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { Modal, ModalFooter } from '../../components/Shared/Modal';
import { useMeSupportStore } from '../../store/useMeSupportStore';
import { useAuthStore } from '../../store/useAuthStore';
import { SupportTicketType } from '../../api/types/admin';

interface Prefill {
  type?: SupportTicketType;
  subject?: string;
  message?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
  prefill?: Prefill;
}

const TYPE_OPTIONS: Array<{ value: SupportTicketType; key: string }> = [
  { value: SupportTicketType.Question, key: 'question' },
  { value: SupportTicketType.Problem, key: 'problem' },
  { value: SupportTicketType.MeetingRequest, key: 'meetingRequest' },
  { value: SupportTicketType.Other, key: 'other' },
];

export function MeSupportCreateModal({ isOpen, onClose, onCreated, prefill }: Props) {
  const { t } = useTranslation(['support', 'common']);
  const { saving, create } = useMeSupportStore();
  const employee = useAuthStore((s) => s.employee);

  const [type, setType] = useState<SupportTicketType>(prefill?.type ?? SupportTicketType.Question);
  const [subject, setSubject] = useState(prefill?.subject ?? '');
  const [message, setMessage] = useState(prefill?.message ?? '');

  // Reset form whenever the modal re-opens (also applies prefill changes).
  useEffect(() => {
    if (isOpen) {
      setType(prefill?.type ?? SupportTicketType.Question);
      setSubject(prefill?.subject ?? '');
      setMessage(prefill?.message ?? '');
    }
  }, [isOpen, prefill?.type, prefill?.subject, prefill?.message]);

  const canSubmit = subject.trim().length > 0 && message.trim().length > 0 && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    // Backend marks email/subject/message as required even on the /me/ route —
    // pull contact info from the authenticated employee so the user doesn't
    // re-enter it. Backend also receives the tenant via cookie claims.
    const id = await create({
      type,
      email: employee?.email ?? '',
      name: employee?.fullName ?? null,
      subject: subject.trim(),
      message: message.trim(),
    });
    if (id) {
      onClose();
      onCreated?.(id);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('support:create.title')}
      size="md"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleSubmit}
          confirmText={t('support:create.submit')}
          cancelText={t('support:create.cancel')}
          confirmDisabled={!canSubmit}
          loading={saving}
        />
      }
    >
      <div className="space-y-3">
        <p className="text-[12px] text-slate-500">{t('support:create.subtitle')}</p>

        <div>
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            {t('support:create.fields.type')}
          </label>
          <select
            value={type}
            onChange={(e) => setType(Number(e.target.value) as SupportTicketType)}
            disabled={saving}
            className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(`support:types.${opt.key}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            {t('support:create.fields.subject')} <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={saving}
            maxLength={200}
            className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
          />
        </div>

        <div>
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            {t('support:create.fields.message')} <span className="text-red-600">*</span>
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={saving}
            rows={5}
            className="w-full px-3 py-2 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 resize-y"
          />
        </div>
      </div>
    </Modal>
  );
}

export default MeSupportCreateModal;
